import { Anchor, Button, Center, Drawer, Stack } from "@mantine/core";

import { Loading } from "@/components/Loading";
import { TextWrapper } from "@/components/TextWrapper";
import { useNewbornFlowsheetSubject } from "@/encounters/components/EncounterLabor/hooks/useNewbornFlowsheetSubject";
import { formatNewbornOptionLabel } from "@/newborn-patient-linking/components/NewbornPicker";

interface NewbornFlowsheetSubjectDrawerProps {
  opened: boolean;
  onClose: () => void;
  questionnaireTitle: string;
  newbornSubject: ReturnType<typeof useNewbornFlowsheetSubject>;
}

/**
 * A newborn-domain entry (Newborn Vitals, Feeding, etc.) needs a resolved newborn subject before the
 * actual questionnaire form is worth showing at all -- renders a lightweight drawer covering the
 * loading/none/ambiguous/error states here instead of the full LaborQuestionnaireDrawer, whose own
 * contract (isLoading, error, etc.) means something different for the other flowsheet types it
 * renders. The caller only renders this while `newbornSubject.status` is one of these 4; the
 * "not-applicable"/"ready" states (flag off, a client-domain questionnaire, or a resolved subject)
 * fall through to the full questionnaire drawer unchanged.
 */
export function NewbornFlowsheetSubjectDrawer({
  opened,
  onClose,
  questionnaireTitle,
  newbornSubject,
}: NewbornFlowsheetSubjectDrawerProps) {
  return (
    <Drawer.Root size={512} offset={8} opened={opened} position="right" onClose={onClose}>
      <Drawer.Overlay />
      <Drawer.Content data-testid="newborn-flowsheet-subject-drawer">
        <Drawer.Header mb="lg" px="md" bg="gray.0">
          <Drawer.CloseButton size="xl" />
          <Drawer.Title c="dark.9">{questionnaireTitle}</Drawer.Title>
        </Drawer.Header>
        <Stack px="lg" py="md" gap="md">
          {newbornSubject.status === "loading" && <Loading height={96} />}
          {newbornSubject.status === "none" && (
            <TextWrapper size="sm" c="gray.7" data-testid="newborn-flowsheet-no-newborn">
              Chart Birthing first to link a newborn before adding newborn flowsheet entries.
            </TextWrapper>
          )}
          {newbornSubject.status === "error" && (
            <Center>
              <Stack gap="sm" align="center">
                <TextWrapper size="sm" c="red.7" data-testid="newborn-flowsheet-subject-error">
                  Couldn&apos;t load this delivery&apos;s newborn.
                </TextWrapper>
                <Anchor
                  type="button"
                  component="button"
                  onClick={newbornSubject.retry}
                  data-testid="newborn-flowsheet-subject-retry"
                >
                  Retry
                </Anchor>
              </Stack>
            </Center>
          )}
          {newbornSubject.status === "ambiguous" && (
            <Stack gap="xs" data-testid="newborn-flowsheet-subject-candidates">
              <TextWrapper size="sm" c="gray.7">
                More than one newborn was found for this delivery — choose which one this entry is
                for.
              </TextWrapper>
              {newbornSubject.candidates.map((candidate) => (
                <Button
                  color="burgundy"
                  variant="outline"
                  key={candidate.patientId}
                  onClick={() => newbornSubject.selectNewborn(candidate.patientReference)}
                  data-testid={`newborn-flowsheet-subject-candidate-${candidate.patientId}`}
                >
                  {formatNewbornOptionLabel(candidate)}
                </Button>
              ))}
            </Stack>
          )}
        </Stack>
      </Drawer.Content>
    </Drawer.Root>
  );
}
